// src/components/firstAider/FeaturedFirstAiderTile.tsx — the featured red
// "Call to First Aiders" tile at the top of the Apps page.
//
// Full-width and visually prominent (its own thing, not a grid AppTile — the
// AppTile tones don't include an emergency red, and the tile is deliberately
// featured). Available to every authenticated user; the AppsPage gates it on
// me.features.firstAiderCallout. Shows the live on-duty count as a caption.

import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { F } from "../../theme";
import { FA, fetchOnDutyCount, registeredSignedInCaption } from "../../lib/firstAider";
import { EmergencyGlyph, FaKeyframes, PulseDot } from "./kit";

export function FeaturedFirstAiderTile() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState<{ registered: number; signedIn: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Fetched on mount, so opening the Apps page always reflects the current
    // roster (including a first aider who's just flagged themselves).
    fetchOnDutyCount()
      .then((res) => {
        if (!cancelled) setCounts({ registered: res.registeredCount, signedIn: res.signedInCount });
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const caption =
    counts === null
      ? "Emergency callout to first aiders"
      : registeredSignedInCaption(counts.registered, counts.signedIn);

  return (
    <>
      <button type="button" onClick={() => navigate("/apps/first-aider")} style={rootStyle}>
        <span style={iconStyle}>
          <EmergencyGlyph size={28} color="#FFFFFF" />
        </span>
        <span style={bodyStyle}>
          <span style={sublabelStyle}>Emergency dispatch</span>
          <span style={labelStyle}>Call to First Aiders</span>
          <span style={countStyle}>
            <PulseDot tone="red" />
            {caption}
          </span>
        </span>
        <span aria-hidden="true" style={chevronStyle}>
          ›
        </span>
      </button>
      <FaKeyframes />
    </>
  );
}

const rootStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "14px 16px",
  width: "100%",
  background: `linear-gradient(180deg, #FFFFFF 0%, ${FA.tint} 100%)`,
  border: `1.5px solid ${FA.red}`,
  borderRadius: 14,
  boxShadow: "0 4px 14px rgba(184,54,42,0.18)",
  cursor: "pointer",
  textAlign: "left",
  position: "relative",
  fontFamily: F.body,
  WebkitTapHighlightColor: "transparent",
};

const iconStyle: CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: 14,
  background: `linear-gradient(135deg, ${FA.red}, ${FA.redDk})`,
  boxShadow: "0 2px 8px rgba(184,54,42,0.25)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
  flexShrink: 0,
};

const bodyStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const sublabelStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: "uppercase",
  color: FA.red,
};

const labelStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 17,
  fontWeight: 700,
  letterSpacing: -0.2,
  color: FA.redDk,
};

const countStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 11.5,
  color: FA.redDk,
  fontWeight: 600,
  lineHeight: 1.3,
  marginTop: 1,
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const chevronStyle: CSSProperties = {
  color: FA.red,
  fontSize: 22,
  fontWeight: 700,
  flexShrink: 0,
};
